import { useState, useEffect, useCallback } from 'react'
import { Row, Col, OverlayTrigger } from 'react-bootstrap'
import { FileUploader } from 'react-drag-drop-files'
import { StyledFileUploader, StyledDropZone, StyledFileTooltip } from './DragDropFileUploader.styled'

const DEFAULT_MAX_FILES = 8
const DEFAULT_MIN_FILES = 0
const HOVER_TEXT = 'Drop to attach files'

const ERROR_TYPE = 'File type is not supported. Please check accepted file types.'
const ERROR_SIZE = 'File size exceeds the maximum allowed limit.'
const ERROR_REQUIRED = 'Attachments are required'
const errorMinFiles = (min: number) => `Minimum ${min} attachments required`
const errorMaxFiles = (max: number) => `Maximum ${max} attachments allowed`

interface IProps {
  onChange: (files: File[]) => void
  acceptedFormats: string[]
  maxFileSizeMB: number
  maxFiles?: number
  minFiles?: number
  required?: boolean
  isSubmitted?: boolean
  disabled?: boolean
}

const DropZone = () => (
  <StyledDropZone className="place-holder">
    <span className="plus-icon">+</span>
    <span>Drag or click to add attachments</span>
  </StyledDropZone>
)

export const DragDropFileUploader = ({
  onChange,
  acceptedFormats,
  maxFileSizeMB,
  maxFiles = DEFAULT_MAX_FILES,
  minFiles = DEFAULT_MIN_FILES,
  required = false,
  isSubmitted = false,
  disabled = false
}: IProps) => {
  const [fileList, setFileList] = useState<File[]>([])
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    onChange(fileList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileList])

  useEffect(() => {
    if (!isSubmitted) return

    if (fileList.length === 0 && minFiles > 0) {
      setErrorMsg(errorMinFiles(minFiles))
      return
    }

    if (fileList.length === 0 && required) {
      setErrorMsg(ERROR_REQUIRED)
      return
    }

    if (minFiles > 0 && fileList.length < minFiles && errorMsg !== ERROR_TYPE && errorMsg !== ERROR_SIZE) {
      setErrorMsg(errorMinFiles(minFiles))
      return
    }

    if (errorMsg === ERROR_REQUIRED || errorMsg.startsWith('Minimum ')) {
      setErrorMsg('')
    }
  }, [isSubmitted, fileList.length, minFiles, required, errorMsg])

  const handleChange = useCallback(
    (newFiles: File | File[]) => {
      const files = newFiles instanceof File ? [newFiles] : Array.from(newFiles)

      if (fileList.length + files.length > maxFiles) {
        setErrorMsg(errorMaxFiles(maxFiles))
        return
      }

      setFileList((prev) => [...prev, ...files])
      setErrorMsg('')
    },
    [fileList.length, maxFiles]
  )

  const handleTypeError = useCallback(() => setErrorMsg(ERROR_TYPE), [])
  const handleSizeError = useCallback(() => setErrorMsg(ERROR_SIZE), [])

  const removeFile = useCallback((file: File) => {
    setFileList((prev) => prev.filter((f) => f !== file))
    setErrorMsg('')
  }, [])

  return (
    <StyledFileUploader className="file-uploader-container" $disabled={disabled}>
      <FileUploader
        handleChange={handleChange}
        multiple
        hoverTitle={HOVER_TEXT}
        name="file"
        types={acceptedFormats}
        maxSize={maxFileSizeMB}
        onTypeError={handleTypeError}
        onSizeError={handleSizeError}
        disabled={disabled}
      >
        <DropZone />
      </FileUploader>

      <div className="error-label">{errorMsg}</div>

      <Row>
        {fileList.map((file, index) => (
          <Col xs={4} key={index}>
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 250, hide: 400 }}
              overlay={<StyledFileTooltip id={`file-tooltip-${index}`}>{file.name}</StyledFileTooltip>}
            >
              <span className="file-name">{file.name}</span>
            </OverlayTrigger>
            <span className="remove-file-btn close btn-close" onClick={() => removeFile(file)}>
              x
            </span>
          </Col>
        ))}
      </Row>
    </StyledFileUploader>
  )
}
